/* core.rules reads and writes.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere in `src/`. Hard rule 1: the edge sets it from a
 * validated token claim. The vite proxy injects it in dev because the proxy stands in for
 * `api-gateway`, which is the only thing permitted to.
 *
 * Every type and guard below comes from `contracts/openapi/rules.yaml` through
 * `tools/openapi_ts/generate.py`. They were hand-written for one commit, which made this file a
 * second source of truth that could drift from the spec silently — a renamed field would have kept
 * compiling and started rendering blanks. `test_generated_client_matches_the_contract` now fails the
 * build if the spec moves and the client does not.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isExecution,
  isExecutionFindings,
  isFailureWindow,
  isPlan,
  isRule,
  isRuleBinding,
  isRuleOverview,
  isRuleVersion,
  isSamplePolicy,
  isTask,
  isViolation,
  isViolationResolution,
  isViolationSample,
} from '@/shared/api/generated/rules';
import type {
  Execution,
  ExecutionFindings,
  FailureWindow,
  Plan,
  Rule,
  RuleBinding,
  RuleKind,
  RuleOverview,
  RuleVersion,
  SamplePolicy,
  Selector,
  Severity,
  Task,
  Violation,
  ViolationResolution,
  ViolationSample,
} from '@/shared/api/generated/rules';
import {
  ApiError,
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
  put,
  remove,
} from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

/* Re-exported so screens and the presentation model import from one place. Without this each screen
 * would reach into `@/shared/api/generated/rules` directly, and the boundary rules would then have no
 * seam to enforce — the feature's API module is where a wire type enters this feature. */
export type {
  Execution,
  ExecutionFindings,
  ExecutionState,
  Plan,
  ResolvedTarget,
  Rule,
  RuleBinding,
  RuleKind,
  RuleOverview,
  RuleState,
  RuleVersion,
  SampleMode,
  SamplePolicy,
  SampleState,
  Selector,
  Severity,
  Task,
  FixKind,
  ResolutionPurpose,
  ResolutionStatement,
  Violation,
  ViolationResolution,
  ViolationSample,
} from '@/shared/api/generated/rules';

const RULES_KEY = ['rules', 'list'] as const;
const BINDINGS_KEY = ['rules', 'bindings'] as const;
const EXECUTIONS_KEY = ['rules', 'executions'] as const;
/* The Rules screen's table. Its own key rather than under `RULES_KEY`, because a binding or an
 * approval changes it without changing the rule listing — so the mutations that move a binding or a
 * request name it as well. */
const OVERVIEW_KEY = ['rules', 'overview'] as const;

/* How much broke in the last day, as four numbers rather than a page of violations.
 *
 * **One aggregate.** The front page's failure card wants a total, a dataset count and a
 * concentration figure; building those from `/violations` means paging every violation in the
 * window, which is thousands of rows on a busy estate to render one sentence.
 */
export function useFailureWindow(): UseQueryResult<FailureWindow> {
  return useQuery({
    queryKey: ['rules', 'violations', 'summary'],
    queryFn: () =>
      getOne('/v1/rules/violations/summary', isFailureWindow, 'failure summary'),
  });
}

/** `enabled: false` means "do not ask", and it is a permission answer rather than a lazy-load.
 *
 * A caller that knows it lacks `read:rules.rule` must not issue the request: a 403 arrives as a
 * failed read, and a feed then has to report an ordinary permission boundary as "we could not find
 * out" — the sentence that has to stay reserved for a genuine fault. Optional and defaulted, so the
 * three existing callers are untouched. */
export interface RulesQueryOptions {
  readonly enabled?: boolean;
}

export function useRules(
  search: string,
  options: RulesQueryOptions = {},
): UseQueryResult<Page<Rule>> {
  const trimmed = search.trim();
  return useQuery({
    /* The search term is part of the key, so a stale result from a previous term cannot be shown
     * under a new one — the failure where a user types, waits, and reads someone else's answer. */
    queryKey: [...RULES_KEY, trimmed],
    queryFn: () =>
      getPage(
        trimmed.length > 0
          ? `/v1/rules?search=${encodeURIComponent(trimmed)}&limit=200`
          : '/v1/rules?limit=200',
        isRule,
        'rule',
      ),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** Every rule beside where it stands, in one read — the Rules screen's table and its strip.
 *
 * **One request for the page, not two per row.** The table needs each rule's latest version and its
 * activation request; asking for them per rule is the fan-out that once exhausted the API's
 * connection pool. `GET /v1/rules/overview` batches them server-side.
 */
export function useRuleOverview(): UseQueryResult<Page<RuleOverview>> {
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => getPage('/v1/rules/overview?limit=200', isRuleOverview, 'rule'),
    retry: false,
  });
}

export function useRule(ruleId: string): UseQueryResult<Rule> {
  return useQuery({
    queryKey: ['rules', 'one', ruleId],
    queryFn: () => getOne(`/v1/rules/${encodeURIComponent(ruleId)}`, isRule, 'rule'),
    retry: false,
  });
}

/* The versions read as options, so a caller with *several* rules can fan over it with `useQueries`.
 *
 * **Extracted rather than copied**, which is the same move `transitionRequestQuery` made one file
 * over and for the same reason: the dataset screen needs one of these per bound rule, a hook cannot
 * be called in a loop, and a second inline query object would be a second answer to "how is a rule's
 * version list fetched" — including its key, which decides whether the two share a cache entry. They
 * do, so a screen that has already listed a rule's versions pays nothing here.
 */
export function ruleVersionsQuery(ruleId: string) {
  return {
    queryKey: ['rules', 'one', ruleId, 'versions'] as const,
    queryFn: () =>
      getPage(
        `/v1/rules/${encodeURIComponent(ruleId)}/versions`,
        isRuleVersion,
        'rule version',
      ),
  };
}

export function useRuleVersions(ruleId: string): UseQueryResult<Page<RuleVersion>> {
  return useQuery({
    ...ruleVersionsQuery(ruleId),
    retry: false,
  });
}

export function useBindings(): UseQueryResult<Page<RuleBinding>> {
  return useQuery({
    queryKey: BINDINGS_KEY,
    queryFn: () => getPage('/v1/rules/bindings?limit=500', isRuleBinding, 'binding'),
    retry: false,
  });
}

/** A rule's name for each rule *version* a binding pins, from the two listings the rules screens
 * already read.
 *
 * **Two cached reads rather than one per rule.** A task carries a rule version id, and the only
 * per-version route is a rule's own version list — asking each rule for its versions is the fan-out
 * that once exhausted the API's connection pool. A binding carries both ids, and every task a run
 * writes comes from a binding, so the bindings listing joined to the rules listing names nearly
 * every task. A version no listed binding pins — its binding was removed since — is absent, and the
 * caller says so in words rather than printing the id.
 */
export function useRuleNamesByVersion(): ReadonlyMap<string, string> {
  const bindings = useBindings();
  const rules = useRules('');
  return useMemo(() => {
    const names = new Map<string, string>();
    const ruleName = new Map(
      (rules.data?.items ?? []).map((rule) => [rule.id, rule.name]),
    );
    for (const binding of bindings.data?.items ?? []) {
      const name = ruleName.get(binding.rule_id);
      if (name !== undefined) names.set(binding.rule_version_id, name);
    }
    return names;
  }, [bindings.data, rules.data]);
}

/** What each recent run found, for the listing's Result column.
 *
 * **Its own request, paired with `useExecutions` by the server rather than by this client.**
 * `/v1/rules/execution-findings` returns one entry per run for the *same* page and in the same
 * order, so a total cannot end up beside the wrong run — which is what pairing two independently
 * limited reads of a table that changes mid-run would eventually do.
 *
 * Polls on the same terms as the listing: a run in flight is exactly when a violation count is worth
 * watching, and a stale total under a moving progress bar is the pair disagreeing on screen.
 */
export function useExecutionFindings(): UseQueryResult<Page<ExecutionFindings>> {
  return useQuery({
    queryKey: [...EXECUTIONS_KEY, 'findings'],
    queryFn: () =>
      getPage(
        '/v1/rules/execution-findings?limit=200',
        isExecutionFindings,
        'execution findings',
      ),
    refetchInterval: (query) => {
      /* Keyed on *this* response rather than on the listing's: a run with tasks still to measure is
         one whose total will move, and it is the only reason to ask again. Reading the listing's
         state here would couple two queries' refetch decisions through React Query's cache. */
      const moving = query.state.data?.items.some(
        (each) => each.measured_tasks < each.total_tasks || each.total_tasks === 0,
      );
      return moving === true ? 1000 : false;
    },
    retry: false,
  });
}

export function useExecutions(): UseQueryResult<Page<Execution>> {
  return useQuery({
    queryKey: EXECUTIONS_KEY,
    queryFn: () => getPage('/v1/rules/executions?limit=200', isExecution, 'execution'),
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.items.some(
        (e) => e.state === 'running' || e.state === 'pending',
      );
      return hasRunning ? 1000 : false;
    },
    retry: false,
  });
}

export interface RuleDraft {
  readonly name: string;
  /* `RuleKind` from the contract, not `string`. The form's option list is derived from it, so a kind
   * added to the spec and not to the form is a type error rather than a select the operator never
   * sees. */
  readonly kind: RuleKind;
  readonly owner: string;
  /** Optional on `RuleCreate`, and carried here so a rule created from a suggestion can say so.
   *
   * **This is the whole of what `core.rules` needs for §2's provenance**, which is why the loop
   * needed no schema change to that module: `POST /v1/rules` already accepts both fields, so
   * `suggested-from:<id>` travels in `tags` and the recommendation's own sentence in `description`.
   * A `suggestion_id` column would have put knowledge of suggestions inside the rules module for a
   * reference a tag already carries.
   */
  readonly description?: string | undefined;
  /** At most 20, each at most 60 characters — `RuleCreate`'s own bounds, refused by the server. */
  readonly tags?: readonly string[] | undefined;
}

export function useCreateRule(): UseMutationResult<Rule, Error, RuleDraft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: RuleDraft) =>
      post('/v1/rules', idempotencyKeyFor('rule', draft.name), isRule, 'rule', draft),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: RULES_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
    retry: false,
  });
}

/** How many live bindings are blocking a delete, or `null` when the refusal was not that one.
 *
 * `useDeleteConnection`'s `datasetsBlockingDelete` exactly: the number travels as a field on the
 * 409, never parsed out of the sentence, so a reworded message or a translation cannot break the
 * screen that reads it. */
export function bindingsBlockingDelete(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const bindings = error.extras['bindings'];
  return typeof bindings === 'number' && Number.isInteger(bindings) && bindings > 0
    ? bindings
    : null;
}

export interface DeleteRuleRequest {
  readonly ruleId: string;
  /** How many live bindings to disable with the rule, or `undefined` to refuse if it has any.
   *
   * **The count, not a flag** — `DeleteConnectionRequest.retireDatasets`'s reasoning: the server
   * checks it against the rule's current live binding count and refuses if they differ, which is
   * what stops a dialog minutes old disabling bindings created in the meantime. So this is only
   * ever the number the server itself just reported. */
  readonly retireBindings?: number;
}

/** Soft-deletes a rule, cascading through its live bindings when the caller confirms how many.
 * `useDeleteConnection`'s shape, applied to bindings instead of datasets. */
export function useDeleteRule(): UseMutationResult<void, Error, DeleteRuleRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, retireBindings }: DeleteRuleRequest) =>
      remove(
        `/v1/rules/${encodeURIComponent(ruleId)}` +
          (retireBindings === undefined
            ? ''
            : `?retire_bindings=${String(retireBindings)}`),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: RULES_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
      // And the bindings namespace: a cascade disables them, so a stale bindings list would show
      // a steward rows that no longer contribute — `useDeleteConnection`'s catalogue invalidation,
      // one namespace over.
      void client.invalidateQueries({ queryKey: BINDINGS_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
    retry: false,
  });
}

export interface RuleEdit {
  readonly ruleId: string;
  /** The complete editable metadata. **A full replacement, matching the route.**
   *
   * Every field is required here even though two are nullable on the wire, and that is the point:
   * an optional `description` in this type would let a caller omit it by accident and clear a
   * steward's text, where a required `string | null` makes clearing it a thing somebody typed. */
  readonly name: string;
  readonly owner: string;
  readonly description: string | null;
  readonly tags: readonly string[];
}

/** Whether a 409 from an edit was the name colliding rather than the rule being deprecated.
 *
 * **Read from the status plus a field, never by matching the sentence** — `bindingsBlockingDelete`'s
 * rule one refusal over: a reworded message or a translation must not change which branch a screen
 * takes. The server distinguishes them in prose, so until it carries a discriminating field this
 * reads the one signal that is structural: a deprecated rule is refused whatever name was sent, so
 * the screen asks the rule it already holds rather than parsing the error. */
export function editRefusedBecauseDeprecated(
  error: unknown,
  rule: Rule | undefined,
): boolean {
  return (
    error instanceof ApiError && error.status === 409 && rule?.state === 'deprecated'
  );
}

/** Edits a rule's metadata. **Cannot touch a version, and the type is why.**
 *
 * `RuleEdit` has no expression field, so there is no value a screen can pass that would reach a
 * `RuleVersion` — the route's body schema is `additionalProperties: false` over the same four
 * fields, so the guarantee holds at both ends. Changing what a rule measures is
 * `usePublishVersion`, which appends a new immutable version.
 *
 * **No idempotency key**, mirroring `put` itself: the rule's id is in the URL and the body is a
 * complete replacement, so sending it twice leaves the same rule in the same state.
 *
 * Invalidates the rule *and* the list: a rename changes what the list renders, and leaving it stale
 * would show a steward the old name in one place and the new one in the other. */
export function useUpdateRule(): UseMutationResult<Rule, Error, RuleEdit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, name, owner, description, tags }: RuleEdit) =>
      put(`/v1/rules/${encodeURIComponent(ruleId)}`, isRule, 'rule', {
        name,
        owner,
        description,
        tags,
      }),
    onSuccess: (updated: Rule) => {
      void client.invalidateQueries({ queryKey: RULES_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
      // `['rules', 'one', id]` is `useRule`'s key. Deliberately not `[...RULES_KEY, id]` — that is
      // `useRules`' *search* key, so invalidating it would refetch a list filtered by the id's text
      // and leave the detail screen showing the name the steward just changed.
      void client.invalidateQueries({ queryKey: ['rules', 'one', updated.id] });
    },
    retry: false,
  });
}

export function useActivateRule(): UseMutationResult<Rule, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const doActivate = async (): Promise<Rule> => {
        try {
          return await post(
            `/v1/rules/${encodeURIComponent(ruleId)}/activate`,
            idempotencyKeyFor('activate', ruleId),
            isRule,
            'rule',
            undefined,
          );
        } catch (err: unknown) {
          const errorMsg = err instanceof Error ? err.message : String(err);

          if (errorMsg.includes('has no published version')) {
            const ruleObj = await getOne(
              `/v1/rules/${encodeURIComponent(ruleId)}`,
              isRule,
              'rule',
            );
            await post(
              `/v1/rules/${encodeURIComponent(ruleId)}/versions`,
              idempotencyKeyFor('auto-pub', `${ruleId}:1.0.0`),
              isRuleVersion,
              'rule version',
              {
                version: '1.0.0',
                expression: {
                  kind: ruleObj.kind,
                  columns: [],
                  parameters: {},
                },
                notes: 'Initial version published for activation',
              },
            );
            return await doActivate();
          }

          throw err;
        }
      };

      return await doActivate();
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['rules'] });
    },
    retry: false,
  });
}

export interface VersionDraft {
  readonly ruleId: string;
  readonly version: string;
  readonly expression: {
    readonly kind: RuleKind;
    readonly columns: readonly string[];
    readonly parameters: Record<string, string>;
  };
  readonly dimension?: string | undefined;
  readonly notes?: string | undefined;
}

export function usePublishVersion(): UseMutationResult<RuleVersion, Error, VersionDraft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: VersionDraft) =>
      post(
        `/v1/rules/${encodeURIComponent(draft.ruleId)}/versions`,
        idempotencyKeyFor('publish-version', `${draft.ruleId}:${draft.version}`),
        isRuleVersion,
        'rule version',
        {
          version: draft.version,
          expression: draft.expression,
          dimension: draft.dimension,
          notes: draft.notes,
        },
      ),
    onSuccess: (_, variables) => {
      void client.invalidateQueries({
        queryKey: ['rules', 'one', variables.ruleId, 'versions'],
      });
      void client.invalidateQueries({ queryKey: RULES_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
    retry: false,
  });
}

export interface BindingDraft {
  readonly ruleId: string;
  readonly ruleVersionId: string;
  readonly selectors: readonly Selector[];
  readonly severity: Severity;
  /** The "Target Column" typed for this specific dataset, independent of the selectors — see
   * `RuleBinding.explicit_columns` on the backend. Only read by the server for a `not_null`/`unique`
   * rule published with no columns; omit or leave empty otherwise, or to check every column the
   * dataset has. */
  readonly explicitColumns?: readonly string[] | undefined;
}

export function useCreateBinding(): UseMutationResult<RuleBinding, Error, BindingDraft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: BindingDraft) =>
      post(
        '/v1/rules/bindings',
        idempotencyKeyFor('create-binding', `${draft.ruleId}:${draft.ruleVersionId}`),
        isRuleBinding,
        'rule binding',
        {
          rule_id: draft.ruleId,
          rule_version_id: draft.ruleVersionId,
          selectors: draft.selectors,
          severity: draft.severity,
          ...(draft.explicitColumns && draft.explicitColumns.length > 0
            ? { explicit_columns: draft.explicitColumns }
            : {}),
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: BINDINGS_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
    retry: false,
  });
}

export interface DisableBindingRequest {
  readonly bindingId: string;
  readonly reason: string;
}

export function useDisableBinding(): UseMutationResult<
  RuleBinding,
  Error,
  DisableBindingRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (req: DisableBindingRequest) =>
      post(
        `/v1/rules/bindings/${encodeURIComponent(req.bindingId)}/disable`,
        idempotencyKeyFor('disable-binding', req.bindingId),
        isRuleBinding,
        'rule binding',
        { reason: req.reason },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: BINDINGS_KEY });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
    retry: false,
  });
}

/** Identifies a plan/execute decision for the idempotency key.
 *
 * `idempotencyKeyFor` memoises per `scope:subject` until explicitly forgotten — right for a form,
 * where a retried click of the same button is a retry of the same decision. Selecting a different
 * set of datasets on the coverage preview is a *different* decision, not a retry of the first, so
 * the subject has to carry the selection: a caller running "just dataset A" and then "just dataset
 * B" must not have the second request treated as a replay of the first because both happened to
 * share the static subject `'all'`, which is what this used to be keyed on.
 */
/** What a run is *of*, for the idempotency key. Sorted, so two clicks in either order are one run. */
function planSubject(request: ExecutionRequest | undefined): string {
  const datasets = request?.datasetIds ?? [];
  const bindings = request?.bindingIds ?? [];
  if (datasets.length === 0 && bindings.length === 0) return 'all';
  /* **Both halves, and prefixed.** A key built from the datasets alone would make "run every rule on
   * this table" and "run *this one rule* on this table" the same subject — so the second click
   * inside the dedup window would be answered with the first run, and a steward who had just bound a
   * rule and pressed Run would be shown a run that predates it. The prefixes keep a binding id and a
   * dataset id from colliding if a future caller passes one list where it meant the other. */
  return [
    `b:${[...bindings].sort().join(',')}`,
    `d:${[...datasets].sort().join(',')}`,
  ].join('|');
}

/** The dry run. Writes nothing and contacts no source — see the contract's own description.
 *
 * A mutation rather than a query even though it changes nothing, because it is a POST: the binding
 * id list is too large for a query string, and TanStack's query cache would otherwise re-run it on
 * every focus and mount. An operator asking "what would this cover" is making a deliberate request,
 * not subscribing to one.
 *
 * `datasetIds`, when given, narrows the coverage preview to a chosen subset of what the rule's
 * bindings resolve to — the coverage table's tick-boxes. Omitted or empty previews everything, which
 * is the platform's long-standing default and stays the default here.
 */
export function usePlanExecution(): UseMutationResult<Plan, Error, string[] | undefined> {
  return useMutation({
    mutationFn: (datasetIds) =>
      post(
        '/v1/rules/executions/plan',
        /* Wrapped rather than passed bare: `planSubject` takes the whole request now, so the
           preview and the run derive their subject from one function and cannot disagree about what
           "the same plan" means. The preview has no binding filter of its own — the coverage table
           narrows by dataset — so this half is `undefined` and the key is unchanged for it. */
        idempotencyKeyFor('plan', planSubject({ datasetIds })),
        isPlan,
        'plan',
        datasetIds && datasetIds.length > 0 ? { dataset_ids: datasetIds } : {},
      ),
    retry: false,
  });
}

/** `datasetIds` is threaded through unchanged from the preview to the run, so a caller cannot end
 * up executing against a wider set than the coverage preview just showed them — the same code path
 * both requests share (see `_plan_arguments` on the backend) is what keeps a preview and its run in
 * agreement.
 */
/** What to run. Both fields optional, and each narrows a different axis of the plan.
 *
 * **`bindingIds` is what makes a per-rule Run honest.** `PlanRequest` has carried it since the route
 * shipped — "omit to plan every enabled binding" — and this hook only ever sent `dataset_ids`, so the
 * one thing a screen could offer was "run everything bound to these tables". A button beside a single
 * rule saying "Run" would then have run every other rule on the dataset too, which is the kind of
 * control that is worse than no control: it does more than it says, on a customer's database.
 *
 * Omitting both is "every enabled binding over everything it resolves to", which is what the
 * Executions screen's own Run button means and is unchanged.
 */
export interface ExecutionRequest {
  readonly datasetIds?: readonly string[] | undefined;
  readonly bindingIds?: readonly string[] | undefined;
}

export function useRequestExecution(): UseMutationResult<
  Execution,
  Error,
  ExecutionRequest | undefined
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request) =>
      post(
        '/v1/rules/executions',
        idempotencyKeyFor('execute', planSubject(request)),
        isExecution,
        'execution',
        {
          /* Omitted rather than sent empty, because the contract's own words for an absent
             `binding_ids` are "plan every enabled binding" — an empty *array* would be a request to
             plan none, which the route refuses as a plan covering nothing. */
          ...(request?.datasetIds && request.datasetIds.length > 0
            ? { dataset_ids: [...request.datasetIds] }
            : {}),
          ...(request?.bindingIds && request.bindingIds.length > 0
            ? { binding_ids: [...request.bindingIds] }
            : {}),
        },
      ),
    onSettled: (_execution, error, request) => {
      /* **A run is a decision, not a form: the key is spent once the server has answered.** Kept
       * memoised, every later press of Run on the same rules re-sent the first press's key — and the
       * workflow runtime uses that key as the workflow id and refuses one it has already run, so the
       * second press was recorded, refused at dispatch, and left Queued behind a 500, with the third
       * press meeting the in-flight 409. Kept only when fetch itself failed (a `TypeError`): then the
       * request may have landed, and re-sending the same key is the retry the key exists for. */
      if (!(error instanceof TypeError)) {
        forgetIdempotencyKey('execute', planSubject(request));
      }
      void client.invalidateQueries({ queryKey: EXECUTIONS_KEY });
    },
    retry: false,
  });
}

/** The reason stored on a run cancelled from the Executions screen, and in its audit entry.
 *
 * Fixed rather than typed, as Unbind's is: the screen offers Cancel only on a run that never
 * started, so the sentence is always true, and a free-text box in front of it would ask somebody
 * to explain a click whose reason the screen already knows. */
export const CANCEL_REASON = 'Cancelled from the Executions screen before it started';

export function useCancelExecution(): UseMutationResult<Execution, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (executionId: string) =>
      post(
        `/v1/rules/executions/${encodeURIComponent(executionId)}/cancel`,
        idempotencyKeyFor('cancel-execution', executionId),
        isExecution,
        'execution',
        { reason: CANCEL_REASON },
      ),
    onSettled: (_execution, error, executionId) => {
      /* Spent once answered, for `useRequestExecution`'s reason; a run is cancelled once, so a
       * second press after an answer is a new question the server answers with a 409. */
      if (!(error instanceof TypeError)) {
        forgetIdempotencyKey('cancel-execution', executionId);
      }
      void client.invalidateQueries({ queryKey: EXECUTIONS_KEY });
    },
    retry: false,
  });
}

/* Rung 4: the tenant's sample policy, and the masked rows behind one violation.
 *
 * `useViolationSample` is `enabled` rather than eager, and that is the point of the hook rather than a
 * performance choice: a sample read is authorized under its own permission and **written to the audit
 * chain**, so fetching one because a row rendered would put a name in a seven-year record for a screen
 * nobody read. It runs when somebody asks.
 *
 * There is no mutation for the policy. `PUT /v1/rules/sample-policy` is admin configuration that
 * decides whether a customer's rows may be copied at all, and it has no UI for the same reason
 * `PUT /v1/ai/serving/route` has none — a form is the wrong affordance for a decision that belongs in
 * a deployment's change record. The policy is *read* here, because "why does this violation have no
 * example rows" has to be answerable on the screen where the question arises.
 */

export function useExecutionViolations(
  executionId: string | undefined,
): UseQueryResult<Page<Violation>> {
  return useQuery({
    queryKey: ['rules', 'executions', executionId, 'violations'],
    queryFn: () =>
      getPage(
        `/v1/rules/executions/${encodeURIComponent(executionId ?? '')}/violations?limit=200`,
        isViolation,
        'violation',
      ),
    enabled: executionId !== undefined,
    retry: false,
  });
}

/** One execution's tasks — the only place the *real* per-dataset failure reason lives.
 *
 * `Execution.detail` is a summary sentence about the whole run ("no task completed…"); the
 * specific reason one dataset could not be measured — a missing rule executor for its connector, a
 * source error, an unsupported column type — is on the task, in `detail`, and was reachable only
 * by curling this route until a screen read it.
 */
export function useExecutionTasks(
  executionId: string | undefined,
): UseQueryResult<Page<Task>> {
  return useQuery({
    queryKey: ['rules', 'executions', executionId, 'tasks'],
    queryFn: () =>
      getPage(
        `/v1/rules/executions/${encodeURIComponent(executionId ?? '')}/tasks?limit=500`,
        isTask,
        'task',
      ),
    enabled: executionId !== undefined,
    /* **Polls while any task is unfinished, which it did not before.** A run's page was static: a
       reader watched a progress bar that had already moved on the server and had to reload to see
       it, which on the one screen whose whole subject is work in flight is the worst place for a
       stale read. Reported from the browser, not found by a gate — nothing can see a missing
       interval.

       Keyed on *this* response rather than on the execution's state, for `useExecutionFindings`'
       reason: a task that has not reached a terminal state is the only thing that makes another
       request worth issuing, and reading the listing's state here would couple two queries'
       refetch decisions through the cache. Stops on its own when every task is done, so a page left
       open on finished history costs nothing. */
    refetchInterval: (query) => {
      const moving = query.state.data?.items.some(
        (task) => task.state === 'pending' || task.state === 'running',
      );
      return moving === true ? 1500 : false;
    },
    retry: false,
  });
}

/** The SQL a steward runs, at their own source, to find and fix one violation's rows.
 *
 * Fetched only when somebody opens the panel — `enabled` — and never retried: a 409 here names why no
 * SQL exists (a custom-SQL rule, an engine this platform cannot render for), and a retry would turn
 * that sentence into a spinner. Lumiq runs none of these statements and sees nothing they return. */
export function useViolationResolution(
  violationId: string | undefined,
): UseQueryResult<ViolationResolution> {
  return useQuery({
    queryKey: ['rules', 'violations', violationId, 'resolution-sql'],
    queryFn: () =>
      getOne(
        `/v1/rules/violations/${encodeURIComponent(violationId ?? '')}/resolution-sql`,
        isViolationResolution,
        'violation resolution',
      ),
    enabled: violationId !== undefined,
    retry: false,
  });
}

/** The newest violation one rule recorded on one table, or none — for a screen that knows the rule
 * and the table but not the run, such as an applied recommendation on the Suggestions screen.
 *
 * The keyset listing is newest first, so the first item of a one-item page *is* the latest. An empty
 * page is a real answer — the rule found nothing on its runs so far, or has not run — and the caller
 * says which it can rather than treating it as a failure. */
export function useLatestViolation(
  ruleId: string | undefined,
  datasetId: string | undefined,
  enabled: boolean,
): UseQueryResult<Violation | null> {
  return useQuery({
    queryKey: ['rules', 'violations', 'latest', ruleId, datasetId],
    queryFn: async () => {
      const query = new URLSearchParams({
        rule_id: ruleId ?? '',
        dataset_id: datasetId ?? '',
        limit: '1',
      });
      const page = await getPage(
        `/v1/rules/violations?${query.toString()}`,
        isViolation,
        'violation',
      );
      return page.items[0] ?? null;
    },
    enabled: enabled && ruleId !== undefined && datasetId !== undefined,
    retry: false,
  });
}

export function useSamplePolicy(): UseQueryResult<SamplePolicy> {
  return useQuery({
    queryKey: ['rules', 'sample-policy'],
    queryFn: () => getOne('/v1/rules/sample-policy', isSamplePolicy, 'sample policy'),
    retry: false,
  });
}

export function useViolationSample(
  violationId: string | undefined,
): UseQueryResult<ViolationSample> {
  return useQuery({
    queryKey: ['rules', 'violations', violationId, 'sample'],
    queryFn: () =>
      getOne(
        `/v1/rules/violations/${encodeURIComponent(violationId ?? '')}/sample`,
        isViolationSample,
        'violation sample',
      ),
    enabled: violationId !== undefined,
    /* Never retried, and this is the one query where that matters most. A 409 here is an *answer* —
     * withheld by policy, past its retention, or never captured — and retrying it would turn three
     * distinct facts about a customer into a spinner. `retry: false` is the house default anyway;
     * stated because the reason is specific to this route. */
    retry: false,
    /* Not cached across mounts. The rows are customer-derived and the read is audited, so holding
     * them in memory after the panel closes would keep masked values in a tab somebody left open —
     * and a second look is a second read, which is what the audit chain should record. */
    gcTime: 0,
    staleTime: 0,
  });
}

/* The two-eyes transition: ask, watch, sign.
 *
 * **Three calls that existed on the server and nowhere in this client.** `POST
 * /transition-requests` and `/transition-decisions` have shipped since `core.rules` was built, and
 * the activation refusal — *"activate requires a satisfied two-eyes approval and none exists. Submit
 * a transition request, collect two signatures from people other than the requester, then retry."* —
 * told a reader to do two things the app gave them no way to do. The GET is new, because there was no
 * way to see whether the first signature had arrived either.
 */

/** The open request for a rule's transition, or `null` when nobody has asked. */
export interface TransitionRequest {
  readonly request_id: string;
  readonly rule_id: string;
  readonly transition: string;
  readonly requester: string;
  readonly required_eyes: number;
  readonly eyes_collected: number;
  readonly satisfied: boolean;
  readonly state: string;
  readonly submitted_at: string;
  readonly decisions: readonly {
    readonly approver: string;
    readonly kind: string;
    readonly decided_at: string;
    readonly reason: string;
  }[];
}

function isTransitionRequest(value: unknown): value is TransitionRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['request_id'] === 'string' &&
    typeof candidate['requester'] === 'string' &&
    typeof candidate['required_eyes'] === 'number' &&
    typeof candidate['eyes_collected'] === 'number' &&
    typeof candidate['satisfied'] === 'boolean' &&
    typeof candidate['state'] === 'string' &&
    Array.isArray(candidate['decisions'])
  );
}

/** The envelope is `{ request: … | null }` rather than a bare request, so "nobody has asked" is a
 * 200 with a null rather than a 404 — the rule exists and the request does not, and a 404 would say
 * the wrong one is missing. */
function isTransitionEnvelope(
  value: unknown,
): value is { readonly request: TransitionRequest | null } {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return candidate['request'] === null || isTransitionRequest(candidate['request']);
}

/** The key and the fetcher for one rule's open activation request, as options rather than as a hook.
 *
 * **Extracted so the notification feed and this rule's own page are literally one fetch.** The feed
 * needs N of these at once, which a hook cannot give it — and the two obvious alternatives are both
 * the shape this repository keeps recording. A second `queryFn` in the header would be a second
 * answer to "what does this envelope mean", and a hand-composed key beside this one would drift the
 * day a parameter is added, with the symptom being a duplicate fetch nobody notices. Sharing the
 * *key* is also what makes the feed free for a reader who already has the rule's page open.
 *
 * Deliberately no `retry` or `staleTime` here: the two consumers want different freshness (a rule's
 * own approval panel wants the current answer after a signature; a header badge does not want to
 * re-ask twenty-five times per navigation) and those are the caller's decision, where the key and the
 * URL are not.
 */
export function transitionRequestQuery(ruleId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<TransitionRequest | null>;
} {
  return {
    queryKey: ['rules', 'transition-request', ruleId, 'activate'],
    queryFn: async () => {
      const envelope = await getOne(
        `/v1/rules/${encodeURIComponent(ruleId)}/transition-requests?action=activate`,
        isTransitionEnvelope,
        'transition request',
      );
      return envelope.request;
    },
  };
}

export function useTransitionRequest(
  ruleId: string,
): UseQueryResult<TransitionRequest | null> {
  return useQuery({
    ...transitionRequestQuery(ruleId),
    enabled: ruleId !== '',
    retry: false,
  });
}

export function useRequestTransition(): UseMutationResult<
  TransitionRequest,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      post(
        `/v1/rules/${encodeURIComponent(ruleId)}/transition-requests`,
        /* Keyed on the rule: the kernel allows one open request per (rule, transition), so a retried
         * POST is the same decision and a second one would be refused anyway. */
        idempotencyKeyFor('rules.transition-request', ruleId),
        isTransitionRequest,
        'transition request',
        { action: 'activate' },
      ),
    onSuccess: (_request, ruleId) => {
      void client.invalidateQueries({
        queryKey: ['rules', 'transition-request', ruleId, 'activate'],
      });
      void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
  });
}

export interface TransitionDecision {
  readonly ruleId: string;
  readonly approved: boolean;
  readonly reason: string;
}

export function useDecideTransition(): UseMutationResult<
  TransitionRequest,
  Error,
  TransitionDecision
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (decision: TransitionDecision) =>
      post(
        `/v1/rules/${encodeURIComponent(decision.ruleId)}/transition-decisions`,
        /* Keyed on the rule *and the verdict*, so a retried approve is one signature and a reject
         * after an approve is a different decision rather than a replay of it. Not keyed on the
         * reason: editing the wording is not a new decision. */
        idempotencyKeyFor(
          `rules.transition-decision.${decision.approved ? 'approve' : 'reject'}`,
          decision.ruleId,
        ),
        isTransitionRequest,
        'transition request',
        {
          action: 'activate',
          approved: decision.approved,
          reason: decision.reason,
        },
      ),
    onSuccess: (_request, decision) => {
      /* The rule itself too: a second signature satisfies the approval, and the *rule* is what a
       * caller then activates — so its state and the list that shows it both need re-reading. */
      void client.invalidateQueries({
        queryKey: ['rules', 'transition-request', decision.ruleId, 'activate'],
      });
      void client.invalidateQueries({ queryKey: ['rules'] });
      forgetIdempotencyKey(
        `rules.transition-decision.${decision.approved ? 'approve' : 'reject'}`,
        decision.ruleId,
      );
    },
  });
}
